<template>
    <span
        v-if="reviewRating"
        class="RatingCount"
        aria-label="Rating: {{ formattedRating }} stars, based on {{ formattedCount }} reviews"
    >
        <b
            v-skeleton="{
                loading,
                sizes: {
                    Small: { width: '1.125rem', height: '0.75rem' },
                }
            }"
        >
            {{ formattedRating }}
        </b>
        <i
            v-skeleton="{
                loading,
                sizes: {
                    Small: { width: '1.75rem', height: '0.75rem' },
                },
                margins: {
                    Small: '0 0.25rem',
                }
            }"
            class="lnr-star-full"
            aria-hidden="true"
        ></i>
        <span v-if="!loading">({{ formattedCount }})</span>
    </span>
</template>

<script setup lang="ts">
import {ReviewRating} from "@app/interfaces/dtos/IReviewRating";

const props = defineProps({
    reviewRating: {
        type: Object as PropType<ReviewRating | null>,
        default: null,
    },
    loading: {
        type: Boolean,
        default: false,
    },
});

import { computed } from 'vue';
import { formatCount } from '@utils/formatCount';

const formattedRating = computed(() => {
    if (!props.reviewRating) return '';

    const formatter = new Intl.NumberFormat('de-DE', {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
    });
    return formatter.format(props.reviewRating.rating);
});

const formattedCount = computed(() => {
    return formatCount(props.reviewRating.amount);
});

</script>

<style scoped lang="scss">
    .RatingCount {
        display: flex;
        color: $Grey-700;

        @include TextStyle(UiSmallRegular);

        b {
            color: $Grey-900;

            @include TextStyle(UiSmallSemibold);
        }

        i {
            color: $Yellow-900;

            @include MarginAreas(0, 0.25);
        }
    }
</style>
